import React, { useEffect, useMemo, useState } from "react"
import { ArrowRight, Check, Database, LoaderCircle, LogOut, RefreshCw, ShieldCheck } from "lucide-react"
import { api } from "../api"
import { useAuth } from "../auth"

const RUNNING_STATES = new Set(["queued", "freezing", "importing"])

export function AccountMigrationPage() {
  const { session, refreshSession, logout } = useAuth()
  const [migration, setMigration] = useState(session.backend_migration)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const running = RUNNING_STATES.has(migration?.state) && !migration?.stale

  useEffect(() => {
    if (!running) return undefined

    let cancelled = false
    const poll = async () => {
      try {
        const status = await api("/account_migration")
        if (cancelled) return
        setMigration(status)
        if (status.state === "completed") await refreshSession()
      } catch (requestError) {
        if (!cancelled) setError(requestError.message)
      }
    }

    const timer = window.setInterval(poll, 1800)
    poll()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [refreshSession, running])

  const progressCopy = useMemo(() => {
    if (migration?.state === "queued") return "Your upgrade is queued and will begin in a moment."
    if (migration?.state === "freezing") return "Preparing your account transfer…"
    if (migration?.state === "importing") return "Moving your teams, players, schedules, and settings…"
    return ""
  }, [migration?.state])

  async function startMigration() {
    setSubmitting(true)
    setError("")
    try {
      setMigration(await api("/account_migration", { method: "POST" }))
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="migration-screen">
      <section className="migration-card" aria-labelledby="migration-title">
        <header className="migration-card__header">
          <img src="/brand/baseball-hub-logo.png" alt="" />
          <div><strong>Baseball Hub</strong><span>Secure account upgrade</span></div>
        </header>

        <div className="migration-card__body">
          <span className="migration-icon"><Database size={29} /></span>
          <p className="eyebrow">A stronger foundation</p>
          <h1 id="migration-title">Let’s move your team to the upgraded app.</h1>
          <p className="migration-lead">We upgraded Baseball Hub’s backend for better reliability and room to grow. We’ll securely move your existing team data before you continue.</p>

          <div className="migration-details">
            <div><Check size={17} /><span><strong>Your password stays the same</strong><small>Sign in normally on web or mobile.</small></span></div>
            <div><Check size={17} /><span><strong>Your team history comes with you</strong><small>Players, games, practices, lineups, and settings are included.</small></span></div>
            <div><ShieldCheck size={17} /><span><strong>The transfer happens securely</strong><small>Your device never receives database credentials.</small></span></div>
          </div>

          {running ? (
            <div className="migration-progress" role="status" aria-live="polite">
              <LoaderCircle className="migration-spinner" size={22} />
              <div><strong>Upgrade in progress</strong><p>{progressCopy}</p></div>
            </div>
          ) : (
            <button className="button button--primary migration-action" type="button" disabled={submitting} onClick={startMigration}>
              {migration?.state === "failed" || migration?.stale ? <RefreshCw size={18} /> : <ArrowRight size={18} />}
              {submitting ? "Starting…" : migration?.state === "failed" || migration?.stale ? "Try the upgrade again" : "Upgrade my account"}
            </button>
          )}

          {migration?.state === "failed" && <p className="migration-error" role="alert">Nothing was deleted. The transfer can be retried safely.</p>}
          {migration?.stale && <p className="migration-error" role="alert">The previous attempt stopped responding. It is safe to try again.</p>}
          {error && <p className="migration-error" role="alert">{error}</p>}

          <button className="migration-signout" type="button" onClick={logout}><LogOut size={15} /> Sign out</button>
        </div>
      </section>
    </main>
  )
}
